import type { CandleWithFlag } from "features/chart";

export interface ADXLines {
  adx: (number | null)[];
  plusDI: (number | null)[];
  minusDI: (number | null)[];
}

export function calcADX(
  data: CandleWithFlag[],
  diLength: number,
  adxSmoothing: number,
): ADXLines {
  const n = data.length;
  const adx: (number | null)[] = new Array(n).fill(null);
  const plusDI: (number | null)[] = new Array(n).fill(null);
  const minusDI: (number | null)[] = new Array(n).fill(null);

  const trArr: number[] = [];
  const pDMArr: number[] = [];
  const mDMArr: number[] = [];

  for (let i = 1; i < n; i++) {
    const c = data[i], p = data[i - 1];
    if (!("high" in c) || !("close" in p)) {
      trArr.push(0); pDMArr.push(0); mDMArr.push(0); continue;
    }
    trArr.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
    const up = c.high - p.high, dn = p.low - c.low;
    pDMArr.push(up > dn && up > 0 ? up : 0);
    mDMArr.push(dn > up && dn > 0 ? dn : 0);
  }

  if (trArr.length < diLength) return { adx, plusDI, minusDI };

  let smTR = trArr.slice(0, diLength).reduce((a, b) => a + b, 0);
  let smPDM = pDMArr.slice(0, diLength).reduce((a, b) => a + b, 0);
  let smMDM = mDMArr.slice(0, diLength).reduce((a, b) => a + b, 0);

  const dxArr: number[] = [];

  const record = (trIdx: number, sTR: number, sPDM: number, sMDM: number) => {
    if (!sTR) return;
    const di = trIdx + 1;
    const pdi = (100 * sPDM) / sTR;
    const mdi = (100 * sMDM) / sTR;
    plusDI[di] = pdi;
    minusDI[di] = mdi;
    dxArr.push(pdi + mdi === 0 ? 0 : (100 * Math.abs(pdi - mdi)) / (pdi + mdi));
  };

  record(diLength - 1, smTR, smPDM, smMDM);
  for (let i = diLength; i < trArr.length; i++) {
    smTR = smTR - smTR / diLength + trArr[i];
    smPDM = smPDM - smPDM / diLength + pDMArr[i];
    smMDM = smMDM - smMDM / diLength + mDMArr[i];
    record(i, smTR, smPDM, smMDM);
  }

  if (dxArr.length < adxSmoothing) return { adx, plusDI, minusDI };

  let smDX = dxArr.slice(0, adxSmoothing).reduce((a, b) => a + b, 0) / adxSmoothing;
  adx[diLength + adxSmoothing - 1] = smDX;
  for (let i = adxSmoothing; i < dxArr.length; i++) {
    smDX = (smDX * (adxSmoothing - 1) + dxArr[i]) / adxSmoothing;
    adx[diLength + i] = smDX;
  }

  return { adx, plusDI, minusDI };
}
